import { useAppTranslation } from 'app/config/i18Config/hooks';
import { SizeCodeForBooking, SizeGroup, SizeCodeType } from 'entities/SizeCode';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { SizeFilterButton } from 'features/BoxSizeFilter/model/types';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

interface UniqueSizeGroup {
  sizeGroup: SizeGroup;

  minBoxSquare: Nullable<number>;
  maxBoxSquare: Nullable<number>;

  minCubicSquare: Nullable<number>;
  maxCubicSquare: Nullable<number>;

  minRegularSquare: Nullable<number>;
  maxRegularSquare: Nullable<number>;

  availableBoxesCount: number[];
}

export const useModernSizeGroupsFilters = (sizeCodes: SizeCodeForBooking[] | undefined): SizeFilterButton[] => {
  const { t } = useAppTranslation('booking');

  const { isDesktop } = useScreenBreakpoints();

  if (!sizeCodes) {
    return [];
  }

  const labelsMap: Record<SizeGroup, string> = {
    [SizeGroup.XS]: `${t('Extra small')} (${SizeGroup.XS})`,
    [SizeGroup.S]: `${t('Small')} (${SizeGroup.S})`,
    [SizeGroup.M]: `${t('Medium')} (${SizeGroup.M})`,
    [SizeGroup.L]: `${t('Large')} (${SizeGroup.L})`,
    [SizeGroup.XL]: `${t('Extra large')} (${SizeGroup.XL})`,
  };

  const getDescription = (group: UniqueSizeGroup): string | undefined => {
    const hasCubic = group.minCubicSquare != null;

    const hasRegular = group.minRegularSquare != null;

    // mixed: cubic + regular
    if (hasCubic && hasRegular) {
      return t(`${isDesktop ? 'Size ' : ''}{{minBoxSquare}} m³ - {{maxBoxSquare}} m²`, {
        minBoxSquare: formatDecimal(group.minCubicSquare),
        maxBoxSquare: formatDecimal(group.maxRegularSquare),
      });
    }

    // cubic only
    if (hasCubic) {
      return t(`${isDesktop ? 'Size ' : ''}{{minBoxSquare}} m³ - {{maxBoxSquare}} m³`, {
        minBoxSquare: formatDecimal(group.minCubicSquare),
        maxBoxSquare: formatDecimal(group.maxCubicSquare),
      });
    }

    const hasSizes = Boolean(group.minRegularSquare) && Boolean(group.maxRegularSquare);

    return hasSizes
      ? t(`${isDesktop ? 'Size ' : ''}{{minBoxSquare}} - {{maxBoxSquare}} m2`, {
          minBoxSquare: formatDecimal(group.minRegularSquare),
          maxBoxSquare: formatDecimal(group.maxRegularSquare),
        })
      : undefined;
  };

  const uniqueSizeGroups: Record<SizeGroup | string, UniqueSizeGroup | never> = {};

  sizeCodes.forEach((sizeCode) => {
    const isCubic = sizeCode.sizeCodeType === SizeCodeType.CUBIC_BOX;

    const isGroupExists = Boolean(uniqueSizeGroups[sizeCode.sizeGroup]);

    if (!isGroupExists) {
      uniqueSizeGroups[sizeCode.sizeGroup] = {
        sizeGroup: sizeCode.sizeGroup,

        minBoxSquare: sizeCode.square,
        maxBoxSquare: sizeCode.square,

        minCubicSquare: isCubic ? sizeCode.square : null,

        maxCubicSquare: isCubic ? sizeCode.square : null,

        minRegularSquare: !isCubic ? sizeCode.square : null,

        maxRegularSquare: !isCubic ? sizeCode.square : null,

        availableBoxesCount: [sizeCode.availableBoxesCount],
      };
    } else {
      const group = uniqueSizeGroups[sizeCode.sizeGroup];

      // keep old logic
      const minBoxSquare = sizeCode.square != null ? Math.min(sizeCode.square, group.minBoxSquare ?? sizeCode.square) : group.minBoxSquare;

      const maxBoxSquare = sizeCode.square != null ? Math.max(sizeCode.square, group.maxBoxSquare ?? sizeCode.square) : group.maxBoxSquare;

      group.minBoxSquare = roundNumber(minBoxSquare);

      group.maxBoxSquare = roundNumber(maxBoxSquare);

      // cubic
      if (isCubic) {
        group.minCubicSquare = roundNumber(Math.min(group.minCubicSquare ?? Infinity, sizeCode.square));

        group.maxCubicSquare = roundNumber(Math.max(group.maxCubicSquare ?? -Infinity, sizeCode.square));
      }

      // regular
      if (!isCubic) {
        group.minRegularSquare = roundNumber(Math.min(group.minRegularSquare ?? Infinity, sizeCode.square));

        group.maxRegularSquare = roundNumber(Math.max(group.maxRegularSquare ?? -Infinity, sizeCode.square));
      }

      group.availableBoxesCount = [...group.availableBoxesCount, sizeCode.availableBoxesCount];
    }
  });

  const sizeGroupOrder: SizeGroup[] = [SizeGroup.XS, SizeGroup.S, SizeGroup.M, SizeGroup.L, SizeGroup.XL];

  const sizeFilterButtons: SizeFilterButton[] = sizeGroupOrder
    .filter((sizeGroup) => uniqueSizeGroups[sizeGroup])
    .map((sizeGroup) => {
      const group = uniqueSizeGroups[sizeGroup];

      return {
        label: labelsMap[group.sizeGroup],
        description: getDescription(group),
        value: group.sizeGroup,
        isDisabled: group.availableBoxesCount.every((count) => count < 1),
      };
    });

  return [
    {
      label: t('All units Sizes'),
      description: t('All sizes'),
      value: 'All',
    },
    ...sizeFilterButtons,
  ];
};
